import React, { Fragment } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import QuickSearchToolbar from "views/components/QuickSearchToolbar";
import moment from "moment";

/**
 * PurchaseListTable
 *
 * Displays a DataGrid showing membership purchase records with integrated QuickSearchToolbar.
 */
const PurchaseListTable = ({
  purchaseList,
  pageSize,
  pageNumber,
  totalRecord,
  setPageSize,
  setPageNumber,
  searchText,
  setSearchText,
  status,
  setStatus,
  paymentType,
  setPaymentType,
}) => {
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPageNumber(0);
  };

  const getStatusChip = (row) => {
    const isExpired = row.end_date && new Date(row.end_date) < new Date();
    const statusNum = Number(row.status);

    if (isExpired || statusNum === 5 || row.status === "expired") {
      return <Chip label="Expired" color="error" size="small" />;
    }
    switch (statusNum) {
      case 2:
        return <Chip label="Active" color="success" size="small" />;
      case 1:
        return <Chip label="Pending" color="warning" size="small" />;
      case 3:
        return <Chip label="Cancelled" color="default" size="small" />;
      case 4:
        return <Chip label="Inactive" color="default" size="small" />;
      case 6:
        return <Chip label="Error" color="error" size="small" />;
      default:
        return <Chip label={row.status || "Unknown"} color="default" size="small" />;
    }
  };

  const columns = [
    {
      flex: 0.5,
      minWidth: 70,
      field: "index",
      headerName: "Index",
    },
    {
      flex: 1.2,
      minWidth: 140,
      field: "member_name",
      headerName: "Member Name",
      renderCell: ({ row }) => {
        const name = `${row.first_name || ""} ${row.last_name || ""}`.trim();
        return <Typography variant="body2">{name || "-"}</Typography>;
      },
    },
    {
      flex: 1.5,
      minWidth: 160,
      field: "email",
      headerName: "Email",
      renderCell: ({ row }) => row.email || "-",
    },
    {
      flex: 1,
      minWidth: 110,
      field: "amount",
      headerName: "Amount (AUD)",
      renderCell: ({ row }) =>
        row.amount !== null && row.amount !== undefined
          ? `${Number(row.amount).toFixed(2)}`
          : "-",
    },
    {
      flex: 1,
      minWidth: 120,
      field: "payment_type",
      headerName: "Payment Type",
      renderCell: ({ row }) =>
        Number(row.payment_type) === 1 ? (
          <Chip label="Recurring" color="info" size="small" variant="outlined" />
        ) : (
          <Chip label="One-Time" color="secondary" size="small" variant="outlined" />
        ),
    },
    {
      flex: 1,
      minWidth: 110,
      field: "status",
      headerName: "Status",
      renderCell: ({ row }) => getStatusChip(row),
    },
    {
      flex: 1.3,
      minWidth: 150,
      field: "start_date",
      headerName: "Purchase Date",
      renderCell: ({ row }) => {
        const formatDate = row.start_date ? moment(row.start_date).format("DD-MM-yyyy") : '-';
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
    {
      flex: 1.3,
      minWidth: 150,
      field: "end_date",
      headerName: "Expiry Date",
      renderCell: ({ row }) => {
        const formatDate = row.end_date ? moment(row.end_date).format("DD-MM-yyyy") : '-';
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
    {
      flex: 1.3,
      minWidth: 150,
      field: "created_at",
      headerName: "Created Date",
      renderCell: ({ row }) => {
        const formatDate = moment(row.created_at).format("DD-MM-yyyy hh:mm A");
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
  ];

  const rows = (purchaseList || []).map((item, index) => ({
    index: index + pageSize * pageNumber + 1,
    ...item,
  }));

  return (
    <Fragment>
      <Card sx={{ width: "100%", overflowInline: "hidden" }}>
        <DataGrid
          hideFooterSelectedRowCount
          autoHeight
          localeText={{ noRowsLabel: "No membership purchases found" }}
          rowCount={Number(totalRecord)}
          page={pageNumber}
          columns={columns}
          rows={rows}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          paginationMode="server"
          onPageChange={(newPage) => setPageNumber(newPage)}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          disableSelectionOnClick
          components={{ Toolbar: QuickSearchToolbar }}
          componentsProps={{
            toolbar: {
              value: searchText,
              clearSearch: () => handleSearch(""),
              onChange: (event) => handleSearch(event.target.value),
              isSearch: true,
              multiDelete: false,
              statusFilter: true,
              statusFilterValue: status,
              onChangeStatusFilter: (e) => {
                setStatus(e.target.value);
                setPageNumber(0);
              },
              paymentTypeFilter: true,
              paymentTypeFilterValue: paymentType,
              onChangePaymentTypeFilter: (e) => {
                setPaymentType(e.target.value);
                setPageNumber(0);
              },
            },
          }}
          sx={{
            ".MuiTablePagination-displayedRows": { marginTop: "1rem" },
            ".MuiTablePagination-selectLabel": { marginTop: "1.1rem" },
            ".MuiInputBase-root": { marginTop: "0.2rem" },
          }}
        />
      </Card>
    </Fragment>
  );
};

export default PurchaseListTable;
